/**
 * PATH: apps/billing/src/main.tsx
 * PURPOSE: Entry point for Billing Hub application
 * ROLE IN ARCHITECTURE: Bootstraps React app with providers
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApiProvider } from '@quotech/api';
import { BillsProvider } from '@quotech/bills';
import { installChunkErrorHandler, installClientErrorReporter } from '@quotech/ui';
import {
  getAuthHeadersForDiagnostics,
  getOrganisationIdForDiagnostics,
} from '@quotech/auth';
import App from './App';
import { setupBillingApi } from './lib/setupApi';
import { createAxiosBillsClient } from './lib/billsClient';
import './index.css';

installChunkErrorHandler();

// Capture runtime errors + failed fetches to /api/diagnostics/client-error/
// so support agents can root-cause opaque crashes without DevTools.
// See packages/ui/src/diagnostics/clientErrorReporter.ts.
installClientErrorReporter({
  buildSha: import.meta.env.VITE_BUILD_SHA,
  getAuthHeaders: getAuthHeadersForDiagnostics,
  getOrganisationId: getOrganisationIdForDiagnostics,
});

setupBillingApi();

// Inject the axios-backed Bills client. Constructed once, after the API
// client is bootstrapped, so it inherits the configured baseURL + auth.
const billsClient = createAxiosBillsClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ApiProvider>
      <BillsProvider client={billsClient}>
        {/* unstable_useTransitions={false} — see apps/newmark/src/main.tsx. (qf!3010, May 2026) */}
        <BrowserRouter unstable_useTransitions={false}>
          <App />
        </BrowserRouter>
      </BillsProvider>
    </ApiProvider>
  </React.StrictMode>
);

